import {
  AtsCandidateFormQuestionTypeEnum,
  type AtsCandidateFormQuestionsOption,
} from '@/generated/graphql/types';
import Upload from '@crehana/compass.icons.outline/Upload';
import { AtsCandidateFormResponseQuestionTypeEnum } from '@crehana/compass.knowledgehub/types';
import { Radio, TextArea } from '@crehana/compass.ui';
import { Checkbox } from '@crehana/compass.ui/checkbox';
import { Input } from '@crehana/compass.ui/input';
import { RadioGroup, RadioGroupItem } from '@crehana/compass.ui/radio-group';
import { cn } from '@crehana/compass.ui/utils';
import { useEffect, useRef, useState } from 'react';
import { useTranslationSharedCandidateForm } from '../i18n/use-translations';
import { UploadCandidateFormAttachments } from './upload-candidate-form-attachments';

type QuestionsProps = {
  type: AtsCandidateFormQuestionTypeEnum;
  response_size?: AtsCandidateFormResponseQuestionTypeEnum;
  value: string | number | undefined | null;
  onChange: (value: string | number | undefined | null) => void;
  allow_multiple_responses?: boolean;
  question_options?: AtsCandidateFormQuestionsOption[];
  error?: boolean;
  question_id: string;
  organizationId: number;
  preview?: boolean;
};

export const Questions = ({
  type,
  value,
  onChange,
  response_size,
  allow_multiple_responses,
  question_options,
  error,
  question_id,
  organizationId,
  preview,
}: QuestionsProps) => {
  const { t } = useTranslationSharedCandidateForm();
  //Question type multiple select with multiple responses
  const handleOptionClick = (optionId: string) => {
    if (allow_multiple_responses && value) {
      const currentValue = value.toString().split(',');
      const newValue = currentValue.includes(optionId)
        ? currentValue.filter(id => id !== optionId)
        : [...currentValue, optionId];
      onChange(newValue.join(','));
    } else {
      onChange(optionId);
    }
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const [maxWidth, setMaxWidth] = useState('auto');

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (containerRef.current) {
      const widths = Array.from(containerRef.current.children).map(
        child => child.getBoundingClientRect().width,
      );
      const max = Math.max(...widths);
      setMaxWidth(`${max}px`);
    }
  }, [question_options]);

  //Question type text
  if (type === AtsCandidateFormQuestionTypeEnum.TextQuestion) {
    return (
      <div className="tw-bg-neutral-light-100 tw-rounded-1.6 tw-border  tw-border-solid tw-border-neutral-light-500 hover:tw-border-primary-light-500 focus:tw-border-primary-light-500">
        <TextArea
          placeholder={t('SPACE_FOR_TEXT')}
          value={value?.toString() || ''}
          onChange={value => onChange(value || '')}
          className="tw-border-none  tw-rounded-1.6 "
          textAreaElProps={{
            rows: 3,
          }}
          sizeType="small"
          error={error}
        />
        <div className="tw-flex tw-justify-end tw-items-center tw-pb-1.2 tw-pr-1.2">
          <span className=" tw-font-body5 tw-text-neutral-light-700">
            {`${value?.toString().length || 0} / ${response_size === AtsCandidateFormResponseQuestionTypeEnum.ShortResponseQuestion ? '70' : '700'}`}
          </span>
        </div>
      </div>
    );
  }

  //Question type dichotomous
  if (type === AtsCandidateFormQuestionTypeEnum.DichotomousQuestion) {
    return (
      <div className="tw-flex tw-pt-3.2">
        <button
          type="button"
          className="tw-w-[13.2rem] tw-bg-neutral-light-100 tw-border tw-border-solid tw-border-neutral-light-500 tw-rounded-0.8 tw-flex tw-items-center tw-p-1.2 tw-mr-1.2"
          onClick={() => onChange(value === t('YES') ? '' : t('YES'))}
        >
          <div className="tw-pointer-events-none">
            <Radio checked={value === t('YES')} size={'s'} />
          </div>
          <span className="tw-font-body4 tw-ml-1.2">{t('YES')}</span>
        </button>
        <button
          type="button"
          className="tw-w-[13.2rem] tw-bg-neutral-light-100 tw-border tw-border-solid tw-border-neutral-light-500 tw-rounded-0.8 tw-flex tw-items-center tw-p-1.2"
          onClick={() => onChange(value === t('NO') ? '' : t('NO'))}
        >
          <div className="tw-pointer-events-none">
            <Radio checked={value === t('NO')} size={'s'} />
          </div>
          <span className="tw-font-body4 tw-ml-1.2">{t('NO')}</span>
        </button>
      </div>
    );
  }

  //Question type select with single response
  if (
    type === AtsCandidateFormQuestionTypeEnum.SelectMultipleQuestion &&
    !allow_multiple_responses
  ) {
    return (
      <div
        className="tw-flex tw-flex-col tw-gap-1.2 tw-pt-3.2 tw-items-start"
        style={{ '--btn-width': maxWidth } as React.CSSProperties}
        ref={containerRef}
      >
        <RadioGroup
          className="tw-grid tw-gap-2"
          value={value?.toString() || ''}
          onValueChange={val => onChange(val)}
        >
          {question_options?.map(option => (
            // biome-ignore lint/a11y/noLabelWithoutControl: <because no detect the radio group item as a input>
            <label
              key={option.id}
              className="tw-bg-neutral-light-100 tw-border tw-border-solid tw-border-neutral-light-500 tw-rounded-0.8 tw-flex tw-items-center tw-p-1.2 tw-w-[var(--btn-width)]"
            >
              <RadioGroupItem size="sm" value={option.id} />
              <span className="tw-font-body4 tw-ml-1.2">{option.label}</span>
            </label>
          ))}
        </RadioGroup>
      </div>
    );
  }

  //Question type multiple select with multiple responses
  if (
    allow_multiple_responses &&
    type === AtsCandidateFormQuestionTypeEnum.SelectMultipleQuestion
  ) {
    return (
      <div
        className="tw-flex tw-flex-col tw-gap-1.2 tw-pt-3.2 tw-items-start"
        style={{ '--btn-width': maxWidth } as React.CSSProperties}
        ref={containerRef}
      >
        {question_options?.map(option => (
          // biome-ignore lint/a11y/noLabelWithoutControl: < because no detect the checkbox as a input>
          <label
            key={option.id}
            className="tw-bg-neutral-light-100 tw-border tw-border-solid tw-border-neutral-light-500 tw-rounded-0.8 tw-flex tw-items-center tw-p-1.2 tw-w-[var(--btn-width)]"
          >
            <Checkbox
              checked={
                allow_multiple_responses &&
                value &&
                value.toString().includes(option.id)
                  ? true
                  : value === option.id
              }
              onCheckedChange={() => handleOptionClick(option.id)}
            />
            <span className="tw-font-body4 tw-ml-1.2">{option.label}</span>
          </label>
        ))}
      </div>
    );
  }

  //Question type file
  if (type === AtsCandidateFormQuestionTypeEnum.FileQuestion) {
    return (
      <div className="tw-pt-0.8">
        {preview ? (
          <div className="tw-btn-primary-xs tw-btn-icon tw-btn-disabled">
            <Upload
              size={16}
              className="tw-text-current group-hover:tw-text-primary-light-500"
            />
            {t('UPLOAD_ATTACHMENT')}
          </div>
        ) : (
          <UploadCandidateFormAttachments
            uploadId={`question_${question_id}`}
            onUpdateAttachment={data => {
              onChange(data?.toString() || '');
            }}
            originalId={organizationId || 0}
          />
        )}
      </div>
    );
  }

  //Question type numeric
  if (type === AtsCandidateFormQuestionTypeEnum.NumericQuestion) {
    return (
      <Input
        type="text"
        placeholder={t('SPACE_FOR_NUMERIC')}
        value={value || ''}
        onChange={e => {
          const newValue = e.target.value;
          if (newValue === '' || /^\d*$/.test(newValue)) {
            onChange(newValue);
          }
        }}
        className={cn(
          'tw-flex-1 tw-bg-neutral-light-100',
          error && 'tw-border-alert-400 focus:tw-border-alert-400',
        )}
      />
    );
  }

  return null;
};
